import { formatLocalDateOnlyForWire } from '@/shared/lib/date-format';
import type {
  PracticeAddress,
  PracticeSetupDetail,
} from '@/types/settings-api';

/**
 * Practice edit form-state helpers (PROD-758).
 *
 * Extracted from the deleted `practice-edit-dialog.tsx` to be reusable
 * across the new route shell + tab components. Pure functions only — no
 * React, no fetch. Tested in `practice-edit-merge.test.ts`.
 */

export const EMPTY_ADDRESS: PracticeAddress = {
  country: '',
  city: '',
  address: '',
  office: '',
  officePhone: '',
  faxNumber: '',
  latitude: '0.0',
  longitude: '0.0',
};

/**
 * Blank PracticeSetupDetail used as the initial draft in create mode and
 * as the fallback shape in edit mode while the fetch is in flight.
 *
 * Defaults match Flutter `PracticeSetupDetail.toJson()` (lines 167-184):
 * - empty strings for nullable text fields
 * - `0.0` lat/lng on the contact section
 * - empty string lat/lng on the billing section (Flutter quirk —
 *   billingDetails accepts `""` whereas contactDetails wants `"0.0"`)
 */
export function emptyDetail(
  practiceSetupId: string,
  organizationId: string,
): PracticeSetupDetail {
  return {
    organizationId,
    practiceSetupId,
    practiceImage: '',
    practiceStampUrl: '',
    practiceName: '',
    licenseNumber: '',
    licenseAuthority: '',
    licenseExpirationDate: '',
    practiceDescription: '',
    primaryPractice: false,
    billingStatus: true,
    billingTaxId: '',
    contactDetails: { ...EMPTY_ADDRESS },
    billingDetails: { ...EMPTY_ADDRESS, latitude: '', longitude: '' },
  };
}

/**
 * Coerce a partial `PracticeSetupDetail` (e.g. from the API) to the
 * controlled-input-friendly shape: nullable strings → `""`, nullable
 * booleans → sensible defaults, missing addresses → empty defaults.
 */
export function normaliseDetail(
  input: Partial<PracticeSetupDetail>,
): PracticeSetupDetail {
  return {
    organizationId: input.organizationId ?? '',
    practiceSetupId: input.practiceSetupId ?? '',
    practiceImage: input.practiceImage ?? '',
    practiceStampUrl: input.practiceStampUrl ?? '',
    practiceName: input.practiceName ?? '',
    licenseNumber: input.licenseNumber ?? '',
    licenseAuthority: input.licenseAuthority ?? '',
    licenseExpirationDate: toNoZWireDate(input.licenseExpirationDate ?? ''),
    practiceDescription: input.practiceDescription ?? '',
    primaryPractice: input.primaryPractice ?? false,
    billingStatus: input.billingStatus ?? true,
    billingTaxId: input.billingTaxId ?? '',
    contactDetails: normaliseAddress(input.contactDetails),
    billingDetails: normaliseAddress(input.billingDetails, true),
  };
}

export function normaliseAddress(
  input: Partial<PracticeAddress> | null | undefined,
  isBilling = false,
): PracticeAddress {
  const latLngDefault = isBilling ? '' : '0.0';
  return {
    country: input?.country ?? '',
    city: input?.city ?? '',
    address: input?.address ?? '',
    office: input?.office ?? '',
    officePhone: input?.officePhone ?? '',
    faxNumber: input?.faxNumber ?? '',
    latitude: input?.latitude ?? latLngDefault,
    longitude: input?.longitude ?? latLngDefault,
  };
}

/**
 * Normalise a license-expiration value to the no-Z wire form the .NET model
 * binder accepts (`YYYY-MM-DDT00:00:00.000`). The binder rejects `…Z` for
 * `licenseExpirationDate` — see CLAUDE.md §API wire-shape contract.
 *
 * PROD-1778: this was `stripZ()` — `iso.endsWith('Z') ? iso.slice(0, -1) : iso`.
 * That is a FALSE safeguard. `toISOString()` converts the instant to UTC
 * *before* the slice, so chopping the `Z` relabels a UTC wall-clock as local
 * instead of converting it; the binder stores the literal and the value lands
 * off by the tz offset. It was harmless here only because every writer already
 * emitted a no-Z string, which made it a no-op that legitimised the pattern by
 * example.
 *
 * `licenseExpirationDate` is a CALENDAR DAY, not an instant, so the correct
 * normalisation extracts the day textually and re-stamps midnight. Deliberately
 * NOT `formatLocalDatetimeForWire`: that re-projects through a timezone, which
 * would move a UTC-midnight value to the previous day for anyone west of UTC.
 * Thin alias over the shared canonical helper, kept for the two settings call
 * sites that already import from this module.
 */
export function toNoZWireDate(iso: string): string {
  return formatLocalDateOnlyForWire(iso);
}

/**
 * Extract the `YYYY-MM-DD` portion of a no-Z ISO string for use as the
 * value of an `<input type="date">`. Uses regex slicing rather than
 * `new Date()` to avoid the UTC round-trip that shifts the date by one
 * day for users east or west of UTC.
 *
 * Accepts `null`/`undefined` so nullable API fields can bind directly — the
 * existing falsy guard already returned `''` for them, the signature just
 * didn't say so. Widened when account-details-page adopted this helper in
 * place of its own copy, which did have the UTC round-trip bug.
 */
export function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const match = iso.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

/**
 * Convert the `YYYY-MM-DD` value of an `<input type="date">` to the wire
 * format the backend expects (`YYYY-MM-DDT00:00:00.000`, no trailing Z).
 */
export function dateInputToWireIso(yyyymmdd: string): string {
  if (!yyyymmdd) return '';
  return `${yyyymmdd}T00:00:00.000`;
}

/**
 * Equality check used to drive the Save-button dirty state. Same pattern
 * as `isStaffDirty` in staff-edit-merge.ts.
 */
export function isDetailDirty(
  source: PracticeSetupDetail,
  draft: PracticeSetupDetail,
): boolean {
  return JSON.stringify(source) !== JSON.stringify(draft);
}
